import { useCallback, useMemo, useState } from "react"
import { Box, Flex, Text, VStack } from "@chakra-ui/react"
import { SupportedTokenName } from "@mezo/sdk"

import fuelIcon from "#/assets/fuel.svg"
import rabbitIcon from "#/assets/rabbit.svg"
import snailIcon from "#/assets/snail.svg"
import zapIcon from "#/assets/zap-green.svg"
import {
  selectDepositActionAmount,
  selectDepositActionMigrationStatus,
  selectDepositActionStatus,
  selectDepositActionType,
  selectDepositActionUserAmount,
  useDappSelector,
} from "#/redux-state"
import CurrencySymbol from "#/shared/components/CurrencySymbol"
import TokenLabel from "#/shared/components/TokenLabel"
import { FRIENDLY_DECIMALS_AMOUNT } from "#/shared/constants"
import {
  useWithdrawalAddress,
  useWithdrawalFee,
  useWithdrawalForm,
} from "#/shared/hooks"
import { DepositMigrationStatus } from "#/shared/types"
import {
  getTokenIcon,
  separateThousandsWithComma,
  trimAddress,
} from "#/shared/utils"

import ReturnAssetButton from "../Buttons/ReturnAssetButton"

import WithdrawalModalAddressInput from "./WithdrawalModalAddressInput"
import WithdrawalModalAddressTag from "./WithdrawalModalAddressTag"
import WithdrawalModalTag from "./WithdrawalModalTag"
import WithdrawalStatus from "./WithdrawalStatus"

const backgroundColors = {
  editable: "primary.black.5",
  featured: "#E0F5E8",
  transparent: "transparent",
}

type WithdrawalModalFormItemProps = {
  token: SupportedTokenName
  destinationAddress: string | null
  destinationLabel: string | null
  isEditable?: boolean
  isDetailed?: boolean
  isBitcoinDeposit?: boolean
  onSuccess: () => void
}

export default function WithdrawalModalFormItem({
  token,
  destinationAddress,
  destinationLabel,
  isEditable = false,
  isDetailed = false,
  isBitcoinDeposit = false,
  onSuccess,
}: WithdrawalModalFormItemProps) {
  const [isActive, setIsActive] = useState(false)
  const userAmount = useDappSelector(selectDepositActionUserAmount)
  const amount = useDappSelector(selectDepositActionAmount)
  const actionType = useDappSelector(selectDepositActionType)
  const migrationStatus = useDappSelector(selectDepositActionMigrationStatus)
  const isDisabled = actionType === "redemption" && token !== "BTC"

  const {
    customDestination,
    isAddressInputVisible,
    setIsAddressInputVisible,
    saveAddress,
  } = useWithdrawalAddress(destinationAddress)

  const { withdraw, isProcessing, isProcessingOtherWithdrawal } =
    useWithdrawalForm(customDestination, token, isBitcoinDeposit, onSuccess)

  const handleWithdraw = useCallback(async () => {
    setIsActive(true)
    await withdraw(amount)
  }, [withdraw, amount])

  const shouldEditDestination = isEditable && !destinationAddress
  const tokenIcon = getTokenIcon(token)

  const { withdrawalFee, thresholdWithdrawalFeeInBTC } = useWithdrawalFee(
    userAmount,
    token,
  )

  const withdrawalStatus = useDappSelector(selectDepositActionStatus)

  const backgroundType = useMemo(() => {
    if (isEditable) {
      return "editable"
    }
    if (isDetailed) {
      return "featured"
    }
    return "transparent"
  }, [isEditable, isDetailed])

  const showBTCFee = !isDetailed && token === "BTC"

  return (
    <Box
      p={backgroundType === "transparent" ? "35px 45px" : "32px"}
      borderRadius="16px"
      bg={backgroundColors[backgroundType]}
      position="relative"
    >
      <Flex flexDir="column" gap={showBTCFee ? "46px" : "16px"}>
        <Flex alignItems="center" justifyContent="space-between">
          <VStack position="relative" marginBottom={showBTCFee ? "-30px" : 0}>
            <TokenLabel
              token={token}
              tokenIcon={tokenIcon}
              amount={userAmount}
              variant="large"
              isMigratedToTbtc={
                migrationStatus === DepositMigrationStatus.Completed
              }
            />
            {showBTCFee && (
              <WithdrawalModalTag variant="small">
                -
                {separateThousandsWithComma(
                  thresholdWithdrawalFeeInBTC,
                  2,
                  FRIENDLY_DECIMALS_AMOUNT,
                )}{" "}
                BTC and network fees
              </WithdrawalModalTag>
            )}
          </VStack>
          {!isDetailed && (
            <>
              <WithdrawalModalTag variant="small" icon={zapIcon}>
                {trimAddress(destinationLabel ?? "")}
              </WithdrawalModalTag>
              <Box width="140px" textAlign="right">
                <ReturnAssetButton
                  onClick={handleWithdraw}
                  isProcessing={isProcessing}
                  isDisabled={isDisabled || isProcessingOtherWithdrawal}
                />
              </Box>
            </>
          )}
        </Flex>
        {isDetailed && (
          <>
            <Flex gap="8px">
              <WithdrawalModalAddressTag
                shouldEditDestination={shouldEditDestination}
                customDestination={customDestination}
                destinationLabel={destinationLabel}
                onClick={() => setIsAddressInputVisible(true)}
              />
              <WithdrawalModalTag
                icon={token === "BTC" ? snailIcon : rabbitIcon}
              >
                {token === "BTC" ? "3-4 hours" : "10 min"}
              </WithdrawalModalTag>
              {withdrawalFee && (
                <WithdrawalModalTag icon={fuelIcon}>
                  ~${separateThousandsWithComma(withdrawalFee, 2)}
                </WithdrawalModalTag>
              )}
              <Box marginLeft="auto">
                <ReturnAssetButton
                  isProcessing={isProcessing}
                  isDisabled={
                    !customDestination ||
                    isDisabled ||
                    isProcessingOtherWithdrawal
                  }
                  onClick={handleWithdraw}
                />
              </Box>
            </Flex>
            {isAddressInputVisible && (
              <WithdrawalModalAddressInput onSave={saveAddress} />
            )}
          </>
        )}
        {token === "BTC" ? (
          <WithdrawalStatus
            withdrawalStatus={
              isActive && !isProcessingOtherWithdrawal
                ? withdrawalStatus
                : "idle"
            }
            onClick={handleWithdraw}
          />
        ) : (
          isActive &&
          withdrawalStatus === "error" && (
            <Text textAlign="right" variant="body" color="primary.red.100">
              Withdraw failed, try again
            </Text>
          )
        )}

        {token !== "BTC" && actionType === "redemption" && (
          <Text textAlign="right" variant="body" color="primary.red.100">
            Withdrawal to <CurrencySymbol token="BTC" /> already started
          </Text>
        )}
      </Flex>
    </Box>
  )
}
